import React, { useState, useEffect } from 'react';
import axios from 'axios';
import dayjs from 'dayjs';
import { Spin, Tooltip } from 'antd';
import {
    FireOutlined,
    RiseOutlined,
    CalendarOutlined,
    FieldTimeOutlined,
    UserOutlined,
    FolderOutlined,
} from '@ant-design/icons';

const STATUS_DOT = {
    Backlog: 'bg-slate-300',
    'In Progress': 'bg-[#28b5fd]',
    'Under Review': 'bg-amber-400',
    Completed: 'bg-emerald-400',
    Closed: 'bg-slate-400',
    Close: 'bg-slate-400',
    'On Hold': 'bg-orange-400',
    Cancelled: 'bg-red-400',
};

const PRIORITY_STYLE = {
    Critical: 'bg-red-100 text-red-700 border border-red-200',
    High: 'bg-orange-50 text-orange-600 border border-orange-200',
    Medium: 'bg-blue-50 text-blue-600 border border-blue-200',
    Low: 'bg-slate-100 text-slate-500 border border-slate-200',
};

function ScoreBar({ score }) {
    const pct = Math.min(100, (score / 100) * 100);
    const color = score >= 80 ? '#ef4444' : score >= 50 ? '#f97316' : '#3b82f6';
    return (
        <div className="flex items-center gap-2">
            <div className="flex-1 h-1.5 rounded-full bg-slate-100 dark:bg-slate-700 overflow-hidden">
                <div
                    className="h-full rounded-full transition-all duration-700"
                    style={{ width: `${pct}%`, backgroundColor: color }}
                />
            </div>
            <span
                className="text-[10px] font-black tabular-nums min-w-[24px] text-right"
                style={{ color }}
            >
                {score}
            </span>
        </div>
    );
}

export default function AIPriorityLeaderboard({ onTaskClick, projectId = null }) {
    const [tasks, setTasks] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        try {
            const url = projectId
                ? `project-management/ai-alerts/priority-leaderboard?projectId=${projectId}&limit=8`
                : `project-management/ai-alerts/priority-leaderboard?limit=8`;
            const { data } = await axios.get(url);
            if (data.success) setTasks(data.tasks || []);
        } catch (e) {
            console.error('[AIPriorityLeaderboard] fetch error', e);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        const interval = setInterval(load, 60000);
        return () => clearInterval(interval);
    }, [projectId]);

    return (
        <div className="flex-1 flex flex-col min-h-0 h-full w-full">
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                    <FireOutlined className="text-orange-500" />
                    <h2 className="text-lg font-black text-[#003263] dark:text-[#a7c8ff] font-headline m-0">
                        AI Priority Ranking
                    </h2>
                </div>
                <Tooltip title="Tasks dynamically ranked by AI risk score. Higher = needs attention first.">
                    <span className="text-xs text-slate-400 cursor-help underline decoration-dotted">How is this calculated?</span>
                </Tooltip>
            </div>

            <div className="flex-1 overflow-y-auto space-y-2 pr-1 custom-scrollbar">
                {loading ? (
                    <div className="flex items-center justify-center h-32">
                        <Spin />
                    </div>
                ) : tasks.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-40 text-center gap-2">
                        <RiseOutlined className="text-2xl text-slate-300" />
                        <p className="text-sm text-slate-500 m-0">No active tasks to rank yet.</p>
                    </div>
                ) : (
                    tasks.map((task, idx) => (
                        <div
                            key={task._id}
                            onClick={() => onTaskClick && onTaskClick(task._id)}
                            className="flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-[#f8f9fa] dark:hover:bg-[#334155] cursor-pointer transition-colors group"
                        >
                            {/* Rank badge */}
                            <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 text-[11px] font-black
                                ${idx === 0 ? 'bg-red-100 text-red-700' : idx === 1 ? 'bg-orange-100 text-orange-600' : idx === 2 ? 'bg-amber-100 text-amber-600' : 'bg-slate-100 text-slate-500'}`}>
                                {idx + 1}
                            </div>

                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-0.5">
                                    <span className={`w-2 h-2 rounded-full flex-shrink-0 ${STATUS_DOT[task.status] || 'bg-slate-300'}`} />
                                    <p className="text-sm font-bold text-[#191c1d] dark:text-white truncate m-0 group-hover:text-[#0057b7] dark:group-hover:text-[#a7c8ff] transition-colors">
                                        {task.title}
                                    </p>
                                </div>

                                <div className="flex items-center gap-3 mb-2">
                                    {task.project && (
                                        <span className="flex items-center gap-1 text-[10px] text-slate-400 truncate">
                                            <FolderOutlined style={{ fontSize: 10 }} />
                                            {task.project.name}
                                        </span>
                                    )}
                                    {task.dueDate && (
                                        <span className={`flex items-center gap-1 text-[10px] font-medium ${dayjs().isAfter(dayjs(task.dueDate)) ? 'text-red-500' : 'text-slate-400'}`}>
                                            <CalendarOutlined style={{ fontSize: 10 }} />
                                            {dayjs(task.dueDate).format('MMM D')}
                                        </span>
                                    )}
                                    {task.assignee && (
                                        <span className="flex items-center gap-1 text-[10px] text-slate-400 truncate">
                                            <UserOutlined style={{ fontSize: 10 }} />
                                            <span className="capitalize">
                                                {`${task.assignee.name || ''}${task.assignee.surname ? ' ' + task.assignee.surname : ''}`.trim().toLowerCase()}
                                            </span>
                                        </span>
                                    )}
                                </div>

                                <ScoreBar score={task.operationalPriority} />
                            </div>

                            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full flex-shrink-0 ${PRIORITY_STYLE[task.priority] || PRIORITY_STYLE.Medium}`}>
                                {task.priority}
                            </span>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
}
